/**
 * Human-friendly labels and file-type helpers for asset conversions (#932).
 *
 * Conversion keys are technical ('480p-video', 'hls-video', 'fs_smart') —
 * these helpers turn them into words a non-technical user recognises, and
 * derive the file extension so the UI can show what kind of file a
 * conversion is (a PSD added by the Adobe panel, a VTT subtitle, ...).
 */

type ConversionLike = {
  content_type?: string | null;
  extension?: string | null;
  key?: string | null;
};

// Mirrors conf/transcodes.py `transcodes_name_map` in the backend, plus
// non-transcode conversion names the pipeline and integrations create.
const NAME_MAP: Record<string, string> = {
  '240p-video': '240p video (web quality)',
  '240p-wm-video': '240p video (watermarked)',
  '320p-video': '320p video (web quality)',
  '320p-wm-video': '320p video (watermarked)',
  '360p-video': '360p video (web quality)',
  '360p-wm-video': '360p video (watermarked)',
  '480p-video': '480p video (SD)',
  '480p-wm-video': '480p video (SD, watermarked)',
  '720p-video': '720p video (HD)',
  '720p-wm-video': '720p video (HD, watermarked)',
  '1080p-video': '1080p video (HD)',
  '1080p-wm-video': '1080p video (HD, watermarked)',
  'hls-video': 'Adaptive streaming (HLS)',
  storyboard: 'Preview storyboard',
  deepzoom: 'Zoomable image (Deep Zoom)',
  clip_preview: 'Clip preview',
  audio: 'Audio track',
  fs_smart: 'Thumbnail',
  wm_fs_smart: 'Thumbnail (watermarked)',
  photoshop_edit: 'Photoshop edit',
  indesign_edit: 'InDesign edit',
  premiere_edit: 'Premiere Pro edit',
  adobe_edit: 'Adobe edit',
  print_ready: 'Print-ready export',
  bg_removed: 'Background removed',
  auto_enhance: 'Auto-enhanced',
};

const SUBTITLE_RE = /^subtitles_([a-z]{2,3}(?:[-_][a-z]{2,4})?)$/i;

// words that read wrong title-cased ('Ai restore' -> 'AI restore')
const ACRONYMS = ['ai', 'hls', 'hd', 'sd', 'cdn', 'dpi', 'pdf', 'psd', 'rgb', 'cmyk', 'ocr'];

/** '480p-video' -> '480p video (SD)'; 'subtitles_en' -> 'Subtitles (EN)';
 *  unknown keys are title-cased with separators turned into spaces. */
export const humanizeConversionName = (name: string): string => {
  if (NAME_MAP[name]) return NAME_MAP[name];

  const subtitle = SUBTITLE_RE.exec(name);
  if (subtitle) return `Subtitles (${subtitle[1].toUpperCase()})`;

  const words = name
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(' ')
    .map((word, index) => {
      if (ACRONYMS.includes(word.toLowerCase())) return word.toUpperCase();
      return index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word;
    });
  return words.join(' ');
};

// Minimal mime -> extension map for conversions whose storage key has no
// usable extension. `content_type` (added to the /data fragment in #932)
// is preferred; the key tail is the fallback for older cached payloads.
const MIME_EXTENSION_MAP: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/avif': 'avif',
  'image/tiff': 'tiff',
  'image/svg+xml': 'svg',
  'image/vnd.adobe.photoshop': 'psd',
  'application/pdf': 'pdf',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'application/x-mpegurl': 'm3u8',
  'application/vnd.apple.mpegurl': 'm3u8',
  'audio/mpeg': 'mp3',
  'audio/mp4': 'm4a',
  'text/vtt': 'vtt',
};

/** Uppercase extension chip text for a conversion, or null when unknown. */
export const conversionExtension = (conversion: ConversionLike): string | null => {
  // 1. backend-provided extension (from the storage key, server-side)
  if (conversion.extension) return conversion.extension.toUpperCase();

  // 2. content_type mapping
  const mime = (conversion.content_type || '').split(';')[0].trim().toLowerCase();
  if (mime && MIME_EXTENSION_MAP[mime]) return MIME_EXTENSION_MAP[mime].toUpperCase();

  // 3. key-tail fallback (older cached /data payloads lack the new fields)
  const key = (conversion.key || '').split('?')[0];
  const tail = key.split('/').pop() || '';
  const dot = tail.lastIndexOf('.');
  if (dot > 0 && dot < tail.length - 1) {
    const ext = tail.slice(dot + 1).toLowerCase();
    if (/^[a-z0-9]{1,5}$/.test(ext)) return ext.toUpperCase();
  }
  return null;
};

/** Multi-file conversions that are streamed/tiled and have no single
 *  downloadable file (the old hard-coded DEEPZOOM/HLS-VIDEO/STORYBOARD list). */
export const isStreamingOnly = (name: string): boolean =>
  ['deepzoom', 'hls-video', 'storyboard'].includes(name.toLowerCase());

/** Video renditions (incl. HLS) served from the video CDN. Falls back to the
 *  derived extension for older cached /data payloads without content_type. */
export const isVideoConversion = (name: string, conversion?: ConversionLike): boolean => {
  if (name.toLowerCase().endsWith('-video')) return true;
  if ((conversion?.content_type || '').startsWith('video')) return true;
  const ext = conversion ? conversionExtension(conversion) : null;
  return !!ext && ['MP4', 'WEBM', 'M3U8', 'MOV'].includes(ext);
};

/** Image renditions — the ones the on-demand imaging endpoint can resize. */
export const isImageConversion = (name: string, conversion: ConversionLike): boolean => {
  if (isVideoConversion(name, conversion)) return false;
  if (isStreamingOnly(name)) return false;
  if (name.toLowerCase().startsWith('subtitles_')) return false;
  if ((conversion.content_type || '').startsWith('image/')) return true;
  const ext = conversionExtension(conversion);
  return !!ext && ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP', 'AVIF', 'TIFF'].includes(ext);
};

/** Conversions the detail-page preview can render in place of the original. */
export const isPreviewableConversion = (name: string, conversion: ConversionLike): boolean => {
  const lower = name.toLowerCase();
  if (['deepzoom', 'storyboard', 'fs_smart', 'wm_fs_smart'].includes(lower)) return false;
  if (lower.startsWith('subtitles_')) return false;
  if (lower === 'hls-video') return true;
  if (isVideoConversion(name, conversion)) return true;
  const contentType = conversion.content_type || '';
  if (contentType.startsWith('image/')) return true;
  const ext = conversionExtension(conversion);
  return !!ext && ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP', 'AVIF'].includes(ext);
};

// The on-demand thumbnail is rendered on request rather than stored, and a
// replace keeps (custom) or redraws (automatic) it — never a format to warn
// about.
const ON_DEMAND_CONVERSIONS = new Set(['smart_imaging', 'fs_smart', 'wm_fs_smart']);

/**
 * The asset's stored ("static") formats — everything a Replace Content deletes
 * (#958): HLS, transcodes, storyboard, deep zoom, image sizes, and anything
 * saved to the asset individually. Backend: nova/apricottasks.py REPLACE
 * deletes "*" except the on-demand image.
 */
export const storedConversions = (conversions: Record<string, unknown> | null | undefined): string[] =>
  Object.keys(conversions ?? {}).filter((name) => !ON_DEMAND_CONVERSIONS.has(name.toLowerCase()));

// Conversions the processing pipeline makes on its own, whatever the
// account's settings list (backend STANDARD_CONVERSIONS + the thumbnail).
const PIPELINE_CONVERSIONS = new Set([
  'deepzoom',
  'storyboard',
  'clip_preview',
  'hls-video',
  'audio',
  'smart_imaging',
  'fs_smart',
  'wm_fs_smart',
]);

type ProcessingSettings = {
  image?: { conversions?: Array<{ key: string; value?: { enabled?: boolean } }> };
  video?: { transcodes?: Record<string, { enabled?: boolean }> };
} | null | undefined;

/**
 * The subset of `storedConversions` a Replace Content destroys for good (#958).
 *
 * Replacing the original deletes EVERY conversion, then reprocesses the new
 * file with the account's settings: the enabled image conversions, the enabled
 * video transcodes and the pipeline's own renditions come back; anything saved
 * to the asset individually (editor saves, cutouts, print exports, Adobe
 * edits, subtitles, ...) does not. Backend: nova/apricottasks.py REPLACE ->
 * delete "*" -> process_file(get_default_processing_keys).
 *
 * The /data payload has no "user-saved" flag, so this goes by name. When in
 * doubt a conversion counts as lost — an unneeded warning is cheap, a missed
 * one deletes someone's work. Without settings (still loading, or failed),
 * only the pipeline names count as regenerated. "Regenerated" is a best
 * guess: it assumes the new file is the same kind (a video replaced by an
 * image gets no transcodes back).
 */
export const conversionsLostOnReplace = (
  conversions: Record<string, unknown> | null | undefined,
  settings: ProcessingSettings,
): string[] => {
  const regenerated = new Set<string>();
  (settings?.image?.conversions ?? []).forEach((c) => {
    if (c?.value?.enabled) regenerated.add(c.key);
  });
  Object.entries(settings?.video?.transcodes ?? {}).forEach(([key, t]) => {
    if (t?.enabled) regenerated.add(key);
  });

  return Object.keys(conversions ?? {}).filter((name) => {
    const lower = name.toLowerCase();
    if (PIPELINE_CONVERSIONS.has(lower) || regenerated.has(name)) return false;
    // A transcode the settings no longer enable still matches here; it is a
    // rendition, not someone's saved work, and a warning would only confuse.
    return !/-(wm-)?video$/.test(lower);
  });
};
